import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Sheet from '@mui/joy/Sheet';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Drawer from '@mui/joy/Drawer';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import Avatar from '@mui/joy/Avatar';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Divider from '@mui/joy/Divider';
import Stack from '@mui/joy/Stack';

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';

import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationDropdown from './NotificationDropdown';

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { notifications } = useNotifications();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = [
    { 
      label: 'Dashboard', 
      icon: <HomeRoundedIcon />, 
      path: '/' 
    },
    { 
      label: 'Seeds', 
      icon: <SpaRoundedIcon />, 
      path: '/seeds' 
    },
    { 
      label: 'Clones', 
      icon: <OpacityRoundedIcon />, 
      path: '/clones' 
    },
    { 
      label: 'Packaging', 
      icon: <LocalShippingRoundedIcon />, 
      path: '/packaging' 
    },
    { 
      label: 'Inventory', 
      icon: <StorefrontRoundedIcon />, 
      path: '/live-listings' 
    },
    { 
      label: 'Settings', 
      icon: <SettingsRoundedIcon />, 
      path: '/settings' 
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Sheet 
      variant="soft" 
      sx={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        backdropFilter: 'blur(20px)',
        backgroundColor: 'rgba(0,0,0,0.9)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}
    >
      <Box 
        sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: 2,
          maxWidth: '100%'
        }}
      >
        {/* Left: Hamburger Menu */}
              <IconButton 
          variant="soft" 
                sx={{
            borderRadius: 'lg',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            color: '#ff9800',
            border: 'none',
                  '&:hover': { 
              transform: 'scale(1.05)',
              transition: 'transform 0.2s',
              backgroundColor: 'rgba(255, 152, 0, 0.2)'
                    }
                  }}
          onClick={() => setDrawerOpen(true)}
        >
          <MenuRoundedIcon />
              </IconButton>

        {/* Center: Company Logo & Name */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            gap: 2,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src="https://homegrown-genetics.com/assets/hgg-small-DG5GGtAZ.png"
              alt="Homegrown Genetics Logo"
              sx={{
                width: 44,
                height: 44,
                objectFit: 'contain'
              }}
            />
                        <Typography 
            level="h4" 
            component="h1" 
                          sx={{ 
              fontWeight: 'bold', 
              color: '#C0C0C0',
              background: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: { xs: 'block', sm: 'block' },
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              textAlign: 'center',
              lineHeight: 1.2
                          }}
                        >
            Homegrown Genetics
                        </Typography>
            </Box>

        {/* Right: Notifications and Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <NotificationDropdown />

          {/* Profile Menu */}
          <Dropdown>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{ 
                root: { 
                  variant: 'soft',
                  sx: {
                    borderRadius: 'lg',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    border: 'none',
                      '&:hover': { 
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)'
                    }
                  }
                } 
                    }}
                  >
                    <Avatar 
                variant="soft"
                      sx={{ 
                  width: 32, 
                  height: 32,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#ffffff'
                      }}
                    >
                <AccountCircleRoundedIcon />
                    </Avatar>
            </MenuButton>
                  <Menu
              variant="soft"
                        sx={{ 
                minWidth: 200,
                background: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 'lg',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                        }}
                      >
              <MenuItem disabled>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PersonRoundedIcon sx={{ color: '#ff9800' }} />
                  <Box>
                    <Typography level="title-sm" sx={{ color: 'white' }}>
                      {user?.attributes?.email || 'User'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      Logged in
                        </Typography>
                    </Box>
                </Stack>
              </MenuItem>
                    <Divider />
                    <MenuItem 
                      onClick={handleSignOut}
                      sx={{ 
                  color: 'danger.500',
                  '&:hover': {
                    bgcolor: 'danger.softBg'
                  }
                      }}
                    >
                <LogoutRoundedIcon sx={{ mr: 1 }} />
                      Sign Out
                    </MenuItem>
                  </Menu>
          </Dropdown>
                </Box>
          </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)}
        anchor="left"
        size="md"
        variant="soft"
        sx={{
          '--Drawer-transitionDuration': '0.4s',
          '--Drawer-transitionTimingFunction': 'cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-content': {
            background: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            border: 'none'
          }
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={2}
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              navigate('/');
              setDrawerOpen(false);
            }}
          >
            <Box
              component="img"
              src="https://homegrown-genetics.com/assets/hgg-small-DG5GGtAZ.png"
              alt="Homegrown Genetics Logo"
              sx={{
                width: 44, 
                height: 44,
                objectFit: 'contain'
              }}
            />
            <Box>
              <Typography 
                level="h4" 
          sx={{ 
                  fontWeight: 'bold', 
                  color: '#C0C0C0',
                  lineHeight: 1.2
                }}
              >
                Homegrown Genetics
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Menu
              </Typography>
            </Box>
          </Stack>
          <IconButton 
            variant="soft" 
            sx={{ 
              borderRadius: 'lg',
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              color: '#ff9800',
              border: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.2)'
              }
            }}
            onClick={() => setDrawerOpen(false)}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Navigation Items */}
        <List sx={{ px: 2, py: 3 }}>
          <AnimatePresence>
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ 
                  delay: index * 0.1,
                  type: 'spring',
                  stiffness: 300,
                  damping: 20
                }}
              >
                <ListItem sx={{ mb: 1 }}>
          <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigation(item.path)}
            sx={{
                      borderRadius: 'lg',
                      gap: 2,
              py: 1.5,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        color: '#ff9800',
                        border: '1px solid rgba(255, 152, 0, 0.3)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.2)',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s'
                      }
                    }}
                  >
                    <Avatar
                      variant="soft"
                sx={{
                        width: 32, 
                        height: 32,
                        backgroundColor: location.pathname === item.path 
                          ? 'rgba(255, 152, 0, 0.2)' 
                          : 'rgba(255, 255, 255, 0.1)',
                        color: location.pathname === item.path 
                          ? '#ff9800' 
                          : '#ffffff'
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Typography 
                      level="title-sm" 
            sx={{
                        fontWeight: 'medium',
                        color: location.pathname === item.path ? '#ff9800' : '#ffffff'
            }}
          >
                      {item.label}
                    </Typography>
          </ListItemButton>
                </ListItem>
              </motion.div>
            ))}
          </AnimatePresence>
        </List>
      </Drawer>
    </Sheet>
  );
}
